/* cspell:disable */
/**
 * MSAL Instance Manager
 *
 * This module manages the singleton MSAL instance for Microsoft Entra ID authentication.
 * It provides a centralized way to create and access the MSAL instance across the application.
 *
 * The singleton pattern ensures that only one MSAL instance is created and reused,
 * which is important for maintaining authentication state and avoiding conflicts.
 */

import { PublicClientApplication } from '@azure/msal-browser';
import { createMsalConfig } from './msalConfig';
import { authLogger } from './utils/logger';

// Singleton MSAL instance
let msalInstance: PublicClientApplication | null = null;

// Store the client ID to detect configuration changes
let currentClientId: string | null = null;

/**
 * Get or create the singleton MSAL instance
 *
 * This function implements the singleton pattern following Microsoft's best practices.
 * It creates a single MSAL instance that is reused throughout the application lifecycle.
 * The instance is recreated if the client ID configuration changes.
 *
 * @returns The singleton PublicClientApplication instance
 * @throws AuthError if MSAL configuration fails
 */
export function getMsalInstance(): PublicClientApplication {
  const msalConfig = createMsalConfig();
  const configClientId = msalConfig.auth.clientId;

  // Create new instance if none exists or configuration changed
  if (!msalInstance || currentClientId !== configClientId) {
    // Log instance creation in development mode
    authLogger.dev(
      '🔄 Creating new MSAL instance with client ID:',
      configClientId,
    );

    msalInstance = new PublicClientApplication(msalConfig);
    currentClientId = configClientId;

    authLogger.success('✅ MSAL instance created successfully');
  }
  // Remove the verbose "reusing instance" log as it's not essential for troubleshooting

  return msalInstance;
}

/**
 * Reset the MSAL instance (primarily for testing purposes)
 *
 * This function allows resetting the singleton instance, which is useful
 * for testing scenarios where you need a fresh instance.
 *
 * @internal This function is intended for testing use only
 * @returns void - This function does not return a value
 */
export function resetMsalInstance(): void {
  msalInstance = null;
  currentClientId = null;
  authLogger.dev('🧹 MSAL instance reset');
}
